import {Component, Inject, OnInit} from '@angular/core';
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {FormControl, FormGroup} from "@angular/forms";
import {
  emptyOverride,
  NameTranslationType, OverrideInstruction, OverridePositionRequest,
  OverridePositionsMap,
  PositionTypeEnum, Product,
  RelatedProductsProgram,
  RelatedProductsProgramOverride,
  UpdateRelatedProductsProgram
} from "app/models/menu.model";
import {Store} from "@ngrx/store";
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {TranslateService} from "@ngx-translate/core";
import {MenuService} from "app/services/menu.service";
import {RpProgramsService} from "app/services/rp-programs.service";
import {BaseModalComponent} from "app/shared/base-modal/base-modal.component";
import * as MenuSelectors from "app/reducers/menu.selectors";
import {Organization} from "app/models/organization.model";
import {MenuOverrideService} from "app/services/menu-override.service";
import {
  SelectProductsModalComponent,
  SelectProductsModalComponentDataType
} from "app/pages/menu/shared/select-products-modal/select-products-modal.component";

@UntilDestroy()
@Component({
  selector: 'app-edit-related-products-v3-modal',
  templateUrl: './edit-related-products-v3-modal.component.html',
  styleUrls: ['./edit-related-products-v3-modal.component.scss']
})
export class EditRelatedProductsV3ModalComponent extends BaseModalComponent implements OnInit {
  initValues = {}
  form: FormGroup;
  overridePositionsMap: OverridePositionsMap;
  relatedProductIds: string[] = [];

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private translate: TranslateService,
    private menuService: MenuService,
    private httpClient: RpProgramsService,
    public dialogRef: MatDialogRef<EditRelatedProductsV3ModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditRpProgramDataType
    ) {
    super(dialogRef);
    this.initValues["rpProgramName"] = data.rpProgram.name;
    this.initValues["relatedProductIds"] = data.rpProgram.products;
    this.form = new FormGroup({});
    this.form.addControl('rpProgramName', new FormControl(data.rpProgram.name ?? ''));
    this.form.addControl('isManuallyCreated', new FormControl({value: this.isManuallyCreated(), disabled: true}));

    if(!data.rpProgram.nameTranslations){
      data.rpProgram.nameTranslations = {} as NameTranslationType;
    }

    for(const lang of this.data.organization.additionalLanguages){
      this.initValues[lang] = data.rpProgram.nameTranslations[lang] ?? null;
      this.form.addControl(lang, new FormControl(data.rpProgram.nameTranslations[lang] ?? ''));
    }

    if(data.rpProgram.products){
      this.relatedProductIds = [...data.rpProgram.products]
    }
  }

  ngOnInit(): void {
    if (!this.isManuallyCreated()) {
      this.store.select(MenuSelectors.selectOverridePositionsMap)
        .pipe(untilDestroyed(this))
        .subscribe(overridePositionsMap => {
          this.overridePositionsMap = overridePositionsMap;
        });
    }
  }

  async submit(){
    const translations: NameTranslationType = {};
    for (const lang of this.data.organization.additionalLanguages) {
      translations[ lang ] = this.form.value[ lang ];
    };

    const requestDto = {
      menuId: this.data.menuId,
      nameTranslations: translations,
      name: this.form.get("rpProgramName").value,
      products: this.relatedProductIds,
    } as UpdateRelatedProductsProgram;

    if (this.data.id == null){
      const response = await this.httpClient.create(requestDto);
      const result = {created: response};
      this.exit(result);
    } else if(this.isManuallyCreated()){
      const response = await this.httpClient.update(this.data.id, requestDto);
      const result = {updated: response};
      this.exit(result);
    } else {
      const result = {updated: {...this.data.rpProgram, ...requestDto}};
      await this.saveOverrides();
      this.exit(result);
    }
  }

  private async saveOverrides(rpProgramId?: string) {
    if(this.data.rpProgram.isManuallyCreated){
      return;
    }
    let override: RelatedProductsProgramOverride;
    let isOverrideExists: boolean;
    let overridePosition = this.overridePositionsMap.getOverride(PositionTypeEnum.RelatedProductsPrograms, this.data.id ?? rpProgramId);

    if(!overridePosition){
      overridePosition = {
        positionType: PositionTypeEnum.RelatedProductsPrograms,
        positionId: this.data.id ?? rpProgramId,
        override: emptyOverride,
        menuSectionId: this.data.menuSectionId,
      };

      override = {};
    } else {
      isOverrideExists = true;
      override = JSON.parse(overridePosition.override) as RelatedProductsProgramOverride;
    }

    const overrideInstructions: OverrideInstruction[] = [];

    if(this.initValues["rpProgramName"]?.trim() !== this.form.get("rpProgramName").value?.trim()){
      if (this.initValues[ 'rpProgramName' ]?.trim() !== this.form.get('rpProgramName').value?.trim()) {
        MenuOverrideService.overrideTextFiled(this.data.rpProgram, this.data.originalRpProgram, 'name', this.form.value[ "rpProgramName" ], isOverrideExists, overrideInstructions);
      }
    }

    if (JSON.stringify(this.relatedProductIds.sort()) !== JSON.stringify(this.initValues["relatedProductIds"].sort())){
      MenuOverrideService.overrideArrayFiled(this.data.rpProgram, this.data.originalRpProgram, 'products', this.relatedProductIds, isOverrideExists, overrideInstructions);
    }

    if (this.data.organization.additionalLanguages) {
      for (const lang of this.data.organization.additionalLanguages) {
        if (this.initValues[ lang ] !== this.form.value[ lang ]) {
          MenuOverrideService.overrideTranslationFiled(this.data.rpProgram, this.data.originalRpProgram, 'nameTranslations', lang, this.form.value[ lang ], isOverrideExists, override, overrideInstructions)
        }
      }
    }

    const menuSectionId = this.data.menuSectionId;
    if (overrideInstructions.length > 0) {

      const overridePositionRequest: OverridePositionRequest = {
        ...overridePosition,
        instructions: overrideInstructions,
      };

      await this.menuService.saveOverridePositions(menuSectionId, [overridePositionRequest], [this.data.menuSectionId])
        .then(_=> _)
        .catch(error => alert(error.error));
    }
  }

  async remove(){
    if(this.data.rpProgram.isManuallyCreated
      && confirm(this.translate.instant('MENU.DISHES.NEW.CONFIRM_DELETE'))) {
      await this.httpClient.delete(this.data.rpProgram.id);
      this.exit({deleted: this.data.rpProgram.id});
    }
  }

  isManuallyCreated() : boolean {
    return this.data.rpProgram.isManuallyCreated;
  }

  restoreName() {
    this.form.get('rpProgramName').setValue(this.data.originalRpProgram.name);
  }

  isNameDifferent() {
    return this.data.originalRpProgram && this.form.value.allergenGroupName !== this.data.originalRpProgram.name;
  }

  editRelatedProducts() {
    const productIds = this.relatedProductIds ?? [];

    this.dialog.open(SelectProductsModalComponent, {
      data: {
        title: this.translate.instant('MENU.DISHES.NEW.SELECT_RELATED_PRODUCTS', {productName: this.data.product.name}),
        productIds: productIds,
      } as SelectProductsModalComponentDataType
    }).afterClosed().subscribe(async res => {
      if (res) {
        const newProductIds = res.data;
        this.relatedProductIds = [...newProductIds];
      }
    });
  }
}

export interface EditRpProgramDataType {
  id: string | null;
  rpProgram: RelatedProductsProgram,
  originalRpProgram: RelatedProductsProgram,
  products: Map<string, Product>,
  organization: Organization,
  product: Product,
  menuSectionId: string,
  menuId: string,
}
